import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity, sanitizePayload, formatImageUrl } from '../../utils/security';
import { toast } from 'react-toastify';
import { 
    Ticket, 
    MessageSquare, 
    Trash2, 
    ExternalLink, 
    ArrowLeft, 
    ChevronLeft, 
    ChevronRight, 
    Send, 
    Image as ImageIcon,
    Clock,
    User,
    Tag,
    X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const AdminTicketDetail = () => {
    const { id } = useParams();
    const { user, isSuperAdmin, loading: authLoading } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [ticket, setTicket] = useState(null);
    const [allTicketIds, setAllTicketIds] = useState([]);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [replyText, setReplyText] = useState('');
    const [replyAttach, setReplyAttach] = useState(null);
    const [replyAttachPreview, setReplyAttachPreview] = useState(null);
    const [isReplying, setIsReplying] = useState(false);
    const [lightboxImages, setLightboxImages] = useState([]);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);

    const openLightbox = (imagesList, startIndex = 0) => {
        setLightboxImages(imagesList);
        setLightboxIndex(startIndex);
        setIsLightboxOpen(true);
    };

    const closeLightbox = () => setIsLightboxOpen(false);

    const prevLightboxImage = useCallback(() => {
        setLightboxIndex(prev => (prev === 0 ? lightboxImages.length - 1 : prev - 1));
    }, [lightboxImages.length]);

    const nextLightboxImage = useCallback(() => {
        setLightboxIndex(prev => (prev === lightboxImages.length - 1 ? 0 : prev + 1));
    }, [lightboxImages.length]);

    useEffect(() => {
        if (!isLightboxOpen) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') closeLightbox();
            if (e.key === 'ArrowLeft') prevLightboxImage();
            if (e.key === 'ArrowRight') nextLightboxImage();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isLightboxOpen, prevLightboxImage, nextLightboxImage]);

    useEffect(() => {
        if (!authLoading && !isSuperAdmin()) navigate('/admin');
    }, [authLoading, isSuperAdmin, navigate]);

    // Fetch list of all ticket IDs for Prev/Next navigation
    useEffect(() => {
        const fetchAllIds = async () => {
            try {
                const res = await fetchWithSecurity(`${API_URL}tickets.php?admin=1`);
                const data = await res.json();
                if (Array.isArray(data)) {
                    setAllTicketIds(data.map(t => parseInt(t.id)));
                }
            } catch (err) {
                console.error('Failed to fetch ticket list for navigation');
            }
        };
        if (user && isSuperAdmin()) fetchAllIds();
    }, [user, isSuperAdmin]);

    // Fetch single ticket details and comments
    const fetchTicketData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchWithSecurity(`${API_URL}tickets.php?ticket_id=${id}&id=${id}`);
            const data = await res.json();
            if (data && !data.error) {
                const ticketData = Array.isArray(data)
                    ? data.find(t => String(t.id) === String(id)) || data[0]
                    : data;

                if (ticketData && ticketData.id) {
                    setTicket(ticketData);
                    setComments(ticketData.comments || []);
                } else {
                    toast.error(t('admin.tickets.toastDetailsError') || 'ტიკეტი ვერ მოიძებნა');
                    navigate('/admin/tickets');
                }
            } else {
                toast.error(t('admin.tickets.toastDetailsError') || 'ტიკეტი ვერ მოიძებნა');
                navigate('/admin/tickets');
            }
        } catch {
            toast.error(t('admin.tickets.toastDetailsError') || 'ტიკეტის ჩატვირთვა ვერ მოხერხდა');
        }
        setLoading(false);
    }, [id, t, navigate]);

    useEffect(() => {
        if (user && id) fetchTicketData();
    }, [user, id, fetchTicketData]);

    const handleStatusChange = async (newStatus) => {
        try {
            const sanitized = sanitizePayload({ status: newStatus });
            const res = await fetchWithSecurity(`${API_URL}tickets.php`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'update_status', id: ticket.id, status: newStatus, ...sanitized })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(t('admin.tickets.toastStatusUpdated') || 'სტატუსი განახლდა');
                setTicket(prev => ({ ...prev, status: newStatus }));
            } else {
                toast.error(data.error || (t('admin.tickets.toastStatusError') || 'სტატუსის განახლება ვერ მოხერხდა'));
            }
        } catch {
            toast.error(t('admin.tickets.toastStatusError') || 'სტატუსის განახლება ვერ მოხერხდა');
        }
    };

    const deleteTicket = async () => {
        if (!window.confirm(t('admin.tickets.deleteConfirm') || 'დარწმუნებული ხართ, რომ გსურთ ამ ტიკეტის წაშლა?')) return;
        try {
            const res = await fetchWithSecurity(`${API_URL}tickets.php?id=${ticket.id}`, { method: 'DELETE' });
            const data = await res.json();
            if (data.success) {
                toast.success(t('admin.tickets.toastDeleted') || 'ტიკეტი წაიშალა');
                navigate('/admin/tickets');
            } else {
                toast.error(data.error || (t('admin.tickets.toastDeleteError') || 'წაშლა ვერ მოხერხდა'));
            }
        } catch {
            toast.error(t('admin.tickets.toastDeleteError') || 'წაშლა ვერ მოხერხდა');
        }
    };

    const handleReplyAttach = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setReplyAttach(file);
        setReplyAttachPreview(URL.createObjectURL(file));
    };

    const submitReply = async () => {
        if (!replyText.trim() && !replyAttach) return;
        setIsReplying(true);
        try {
            let attachmentUrl = '';
            if (replyAttach) {
                const formData = new FormData();
                formData.append('image', replyAttach);
                const upRes = await fetchWithSecurity(`${API_URL}upload.php`, { method: 'POST', body: formData });
                const upData = await upRes.json();
                if (upData.success) attachmentUrl = upData.url;
            }
            const sanitized = sanitizePayload({ message: replyText, attachment_url: attachmentUrl });
            const res = await fetchWithSecurity(`${API_URL}tickets.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'add_comment', ticket_id: ticket.id, user_id: user?.id, ...sanitized })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(t('admin.tickets.toastReplySent') || 'პასუხი გაიგზავნა');
                setReplyText('');
                setReplyAttach(null);
                setReplyAttachPreview(null);
                fetchTicketData();
            } else {
                toast.error(data.error || (t('admin.tickets.toastReplyError') || 'პასუხის გაგზავნა ვერ მოხერხდა'));
            }
        } catch {
            toast.error(t('admin.tickets.toastReplyError') || 'პასუხის გაგზავნა ვერ მოხერხდა');
        }
        setIsReplying(false);
    };

    // Prev & Next navigation logic
    const currentIdNum = parseInt(id);
    const currentIndex = allTicketIds.indexOf(currentIdNum);
    const prevTicketId = currentIndex > 0 ? allTicketIds[currentIndex - 1] : null;
    const nextTicketId = currentIndex >= 0 && currentIndex < allTicketIds.length - 1 ? allTicketIds[currentIndex + 1] : null;

    if (authLoading || !user) return null;

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'Open': return 'shadcn-badge-warning';
            case 'In Progress': return 'shadcn-badge-info';
            case 'Resolved': return 'shadcn-badge-success';
            case 'Closed': return 'shadcn-badge-danger';
            default: return 'shadcn-badge-primary';
        }
    };

    return (
        <AdminLayout title={ticket && ticket.id ? `${t('admin.tickets.detailTitle') || 'ტიკეტის დეტალები'} #${ticket.id}: ${ticket.subject || ''}` : (t('admin.tickets.detailTitle') || 'ტიკეტის დეტალები')}>
            {/* ── TOP ACTION BAR: BACK & PREV / NEXT BUTTONS ── */}
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-3 mb-4">
                <button className="shadcn-btn shadcn-btn-secondary" onClick={() => navigate('/admin/tickets')}>
                    <ArrowLeft size={16} /> {t('common.back') || 'უკან დაბრუნება'}
                </button>

                {/* PREV & NEXT NAVIGATION BUTTONS */}
                <div className="d-flex align-items-center gap-2">
                    <button 
                        className="shadcn-btn shadcn-btn-secondary" 
                        onClick={() => prevTicketId && navigate(`/admin/tickets/detail/${prevTicketId}`)}
                        disabled={!prevTicketId}
                        title={t('admin.tickets.prevTicket') || 'წინა ტიკეტი'}>
                        <ChevronLeft size={16} /> {t('common.prev') || 'წინა'}
                    </button>
                    <span className="text-muted small px-1">
                        {currentIndex >= 0 ? `${currentIndex + 1} / ${allTicketIds.length}` : '—'}
                    </span>
                    <button 
                        className="shadcn-btn shadcn-btn-secondary" 
                        onClick={() => nextTicketId && navigate(`/admin/tickets/detail/${nextTicketId}`)}
                        disabled={!nextTicketId}
                        title={t('admin.tickets.nextTicket') || 'შემდეგი ტიკეტი'}>
                        {t('common.next') || 'შემდეგი'} <ChevronRight size={16} />
                    </button>
                </div>
            </div>

            {loading || !ticket ? (
                <div className="text-center py-5 text-muted">
                    <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                    <span>{t('admin.tickets.loadingTickets') || 'ტიკეტის დეტალები იტვირთება...'}</span>
                </div>
            ) : (
                <div className="shadcn-card max-w-4xl mx-auto">
                    {/* Header Details Box */}
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 pb-4 mb-4 border-bottom">
                        <div className="d-flex align-items-start gap-3">
                            <div className="p-3 rounded-3 bg-indigo-50 text-primary flex-shrink-0">
                                <Ticket size={24} />
                            </div>
                            <div>
                                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                    <span className="text-muted small">#{ticket.id}</span>
                                    <h3 className="text-dark fs-5 fw-bold mb-0">{ticket.subject}</h3>
                                </div>
                                <div className="d-flex gap-3 text-muted smallest flex-wrap mt-2">
                                    <span className="d-flex align-items-center gap-1"><Tag size={13} /> {t('admin.tickets.colCategory') || 'კატეგორია'}: <strong className="text-dark">{ticket.category_name}</strong></span>
                                    <span className="d-flex align-items-center gap-1"><User size={13} /> {t('admin.tickets.colClient') || 'კლიენტი'}: <strong className="text-dark">{ticket.username}</strong></span>
                                    <span className="d-flex align-items-center gap-1"><Clock size={13} /> {ticket.created_at ? new Date(ticket.created_at).toLocaleString() : ''}</span>
                                </div>
                            </div>
                        </div>

                        {/* Status Change & Actions */}
                        <div className="d-flex align-items-center gap-2 flex-shrink-0">
                            <select
                                className={`shadcn-select form-select-sm py-1 ${getStatusBadgeClass(ticket.status)}`}
                                value={ticket.status}
                                onChange={(e) => handleStatusChange(e.target.value)}
                                style={{ maxWidth: '150px' }}
                            >
                                <option value="Open">{t('admin.tickets.statusOpen') || 'ღია'}</option>
                                <option value="In Progress">{t('admin.tickets.statusInProgress') || 'მიმდინარე'}</option>
                                <option value="Resolved">{t('admin.tickets.statusResolved') || 'გადაწყვეტილი'}</option>
                                <option value="Closed">{t('admin.tickets.statusClosed') || 'დახურული'}</option>
                            </select>

                            <Link to={`/support/ticket/${ticket.id}`} target="_blank" className="shadcn-btn shadcn-btn-sm shadcn-btn-secondary" title={t('admin.tickets.viewAsUser') || 'მომხმარებლის ხედვა'}>
                                <ExternalLink size={14} />
                            </Link>
                            <button className="shadcn-btn shadcn-btn-sm shadcn-btn-danger" onClick={deleteTicket} title={t('admin.tickets.delete') || 'წაშლა'}>
                                <Trash2 size={14} />
                            </button>
                        </div>
                    </div>

                    {/* Original Message Card */}
                    <div className="p-4 rounded-3 bg-slate-50 border mb-4">
                        <div className="text-muted smallest mb-2 fw-semibold text-uppercase tracking-wider">{t('admin.tickets.originalMessage') || 'ორიგინალი შეტყობინება:'}</div>
                        <p className="text-dark mb-0 small leading-relaxed" style={{ whiteSpace: 'pre-wrap' }}>{ticket.message}</p>
                        {ticket.attachment_url && (
                            <div className="mt-3">
                                <div 
                                    className="cursor-pointer d-inline-block position-relative shadow-sm rounded-3 overflow-hidden border"
                                    style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                    onClick={() => {
                                        const list = [];
                                        if (ticket.attachment_url) list.push({ url: formatImageUrl(ticket.attachment_url), title: `${ticket.subject || 'Ticket'} #${ticket.id}`, sender: ticket.username, date: ticket.created_at });
                                        comments.forEach((c, idx) => {
                                            if (c.attachment_url) list.push({ url: formatImageUrl(c.attachment_url), title: `${t('admin.tickets.replyLabel') || 'Reply'} #${idx + 1}`, sender: c.username, date: c.created_at });
                                        });
                                        openLightbox(list, 0);
                                    }}
                                    title="გადიდება (Lightbox)"
                                >
                                    <img 
                                        src={formatImageUrl(ticket.attachment_url)} 
                                        alt="Attachment" 
                                        style={{ maxWidth: '220px', maxHeight: '150px', objectFit: 'cover', borderRadius: '8px', display: 'block' }} 
                                    />
                                    <span className="position-absolute bottom-0 end-0 bg-dark bg-opacity-75 text-white smallest px-2 py-1 rounded-top-start">
                                        🔍 Lightbox
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Replies & Thread Discussion */}
                    <h5 className="text-dark fs-6 fw-bold mb-3 d-flex align-items-center gap-2">
                        <MessageSquare size={18} className="text-primary" />
                        <span>{t('admin.tickets.threadTitle') || 'მიმოწერის ისტორია'}</span>
                        <span className="badge bg-slate-100 text-slate-700 rounded-pill fs-7">{comments.length}</span>
                    </h5>

                    {comments.length > 0 ? (
                        <div className="d-flex flex-column gap-3 mb-4">
                            {comments.map((c, cIdx) => (
                                <div 
                                    key={c.id} 
                                    className={`p-3 rounded-3 border ${c.role === 'super_admin' ? 'bg-primary bg-opacity-10 border-primary border-opacity-30' : 'bg-white shadow-sm'}`}
                                >
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-dark small fw-bold d-flex align-items-center gap-2">
                                            {c.username}
                                            {c.role === 'super_admin' && (
                                                <span className="shadcn-badge shadcn-badge-primary">{t('admin.tickets.badgeSupport') || 'მხარდაჭერის გუნდი'}</span>
                                            )}
                                        </span>
                                        <span className="text-muted smallest">{c.created_at ? new Date(c.created_at).toLocaleString() : ''}</span>
                                    </div>
                                    <p className="text-dark mb-0 small" style={{ whiteSpace: 'pre-wrap' }}>{c.message}</p>
                                    {c.attachment_url && (
                                        <div className="mt-2">
                                            <div 
                                                className="cursor-pointer d-inline-block position-relative shadow-sm rounded-2 overflow-hidden border"
                                                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                                onClick={() => {
                                                    const list = [];
                                                    if (ticket.attachment_url) list.push({ url: formatImageUrl(ticket.attachment_url), title: `${ticket.subject || 'Ticket'} #${ticket.id}`, sender: ticket.username, date: ticket.created_at });
                                                    comments.forEach((item, idx) => {
                                                        if (item.attachment_url) list.push({ url: formatImageUrl(item.attachment_url), title: `${t('admin.tickets.replyLabel') || 'Reply'} #${idx + 1}`, sender: item.username, date: item.created_at });
                                                    });
                                                    const targetUrl = formatImageUrl(c.attachment_url);
                                                    const idx = list.findIndex(item => item.url === targetUrl);
                                                    openLightbox(list, idx >= 0 ? idx : 0);
                                                }}
                                                title="გადიდება (Lightbox)"
                                            >
                                                <img src={formatImageUrl(c.attachment_url)} alt="Attachment" style={{ maxWidth: '180px', maxHeight: '120px', objectFit: 'cover', borderRadius: '6px', display: 'block' }} />
                                                <span className="position-absolute bottom-0 end-0 bg-dark bg-opacity-75 text-white smallest px-2 py-0 rounded-top-start">
                                                    🔍
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-muted small mb-4 p-3 bg-light rounded-3 text-center">{t('admin.tickets.noReplies') || 'პასუხები ჯერ არ არის.'}</p>
                    )}

                    {/* Reply Form */}
                    {ticket.status !== 'Closed' ? (
                        <div className="pt-4 border-top">
                            <label className="form-group-label mb-2 fw-bold text-dark">{t('admin.tickets.replyLabel') || 'ოფიციალური პასუხის დაწერა:'}</label>
                            <textarea
                                className="shadcn-textarea mb-3"
                                rows="3"
                                placeholder={t('admin.tickets.replyPlaceholder') || 'დაწერეთ ოფიციალური პასუხი...'}
                                value={replyText}
                                onChange={e => setReplyText(e.target.value)}
                            />
                            {replyAttachPreview && (
                                <div className="mb-3 d-flex align-items-center gap-2">
                                    <img src={replyAttachPreview} alt="preview" style={{ width: '80px', height: '60px', objectFit: 'cover', borderRadius: '6px' }} />
                                    <button type="button" className="shadcn-btn shadcn-btn-sm shadcn-btn-danger" onClick={() => { setReplyAttach(null); setReplyAttachPreview(null); }}>
                                        {t('common.remove') || 'წაშლა'}
                                    </button>
                                </div>
                            )}
                            <div className="d-flex justify-content-between align-items-center">
                                <label className="shadcn-btn shadcn-btn-secondary cursor-pointer mb-0">
                                    <ImageIcon size={16} /> {replyAttach ? replyAttach.name : (t('admin.tickets.attachImage') || 'ფაილის მიბმა')}
                                    <input type="file" accept="image/*" onChange={handleReplyAttach} style={{ display: 'none' }} />
                                </label>
                                <button className="shadcn-btn shadcn-btn-primary" onClick={submitReply} disabled={isReplying || (!replyText.trim() && !replyAttach)}>
                                    <Send size={16} /> {isReplying ? (t('admin.tickets.sending') || 'იგზავნება...') : (t('admin.tickets.sendReply') || 'პასუხის გაგზავნა')}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="p-3 bg-danger bg-opacity-10 text-danger rounded-3 text-center fw-semibold small">
                            {t('admin.tickets.closedNotice') || 'ეს ტიკეტი დახურულია. პასუხის გასაგზავნად შეცვალეთ ტიკეტის სტატუსი.'}
                        </div>
                    )}
                </div>
            )}

            {/* ── LIGHTBOX MODAL WITH PREV/NEXT CONTROLS ── */}
            {isLightboxOpen && lightboxImages.length > 0 && (
                <div 
                    className="orbx-lightbox-overlay" 
                    onClick={closeLightbox}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(5, 8, 22, 0.94)',
                        backdropFilter: 'blur(10px)',
                        zIndex: 999999,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '24px'
                    }}
                >
                    {/* Top Bar */}
                    <div 
                        className="d-flex justify-content-between align-items-center w-100"
                        style={{ maxWidth: '1200px', zIndex: 1000000 }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="text-white">
                            <span className="fw-bold fs-6 d-block text-white">
                                {lightboxImages[lightboxIndex]?.title}
                            </span>
                            <span className="smallest text-white-50">
                                {lightboxImages[lightboxIndex]?.sender} • {lightboxImages[lightboxIndex]?.date ? new Date(lightboxImages[lightboxIndex].date).toLocaleString() : ''}
                            </span>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <span className="badge rounded-pill bg-white bg-opacity-10 text-white px-3 py-2 fw-semibold fs-7 border border-white border-opacity-10">
                                {lightboxIndex + 1} / {lightboxImages.length}
                            </span>
                            <button
                                type="button"
                                onClick={closeLightbox}
                                className="btn btn-outline-light rounded-circle p-2 d-flex align-items-center justify-content-center"
                                style={{ width: '42px', height: '42px', border: '1px solid rgba(255,255,255,0.2)' }}
                                title={t('common.close') || 'Close'}
                            >
                                <X size={22} />
                            </button>
                        </div>
                    </div>

                    {/* Main Image Viewer */}
                    <div 
                        className="position-relative d-flex align-items-center justify-content-center flex-grow-1 w-100 my-3"
                        style={{ minHeight: 0 }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Prev Button */}
                        {lightboxImages.length > 1 && (
                            <button
                                type="button"
                                onClick={prevLightboxImage}
                                className="btn position-absolute start-0 top-50 translate-middle-y text-white rounded-circle d-flex align-items-center justify-content-center shadow-lg"
                                style={{
                                    zIndex: 1000001,
                                    width: '54px',
                                    height: '54px',
                                    background: 'rgba(255, 255, 255, 0.15)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    cursor: 'pointer'
                                }}
                                title={t('common.prev') || 'Previous'}
                            >
                                <ChevronLeft size={30} />
                            </button>
                        )}

                        <div className="d-flex align-items-center justify-content-center h-100 w-100 p-2">
                            <img
                                src={lightboxImages[lightboxIndex]?.url}
                                alt={lightboxImages[lightboxIndex]?.title || 'Attachment'}
                                style={{
                                    maxHeight: '78vh',
                                    maxWidth: '82vw',
                                    objectFit: 'contain',
                                    borderRadius: '12px',
                                    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.6)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)'
                                }}
                            />
                        </div>

                        {/* Next Button */}
                        {lightboxImages.length > 1 && (
                            <button
                                type="button"
                                onClick={nextLightboxImage}
                                className="btn position-absolute end-0 top-50 translate-middle-y text-white rounded-circle d-flex align-items-center justify-content-center shadow-lg"
                                style={{
                                    zIndex: 1000001,
                                    width: '54px',
                                    height: '54px',
                                    background: 'rgba(255, 255, 255, 0.15)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    cursor: 'pointer'
                                }}
                                title={t('common.next') || 'Next'}
                            >
                                <ChevronRight size={30} />
                            </button>
                        )}
                    </div>

                    {/* Bottom Thumbnails Strip */}
                    {lightboxImages.length > 1 && (
                        <div 
                            className="d-flex gap-2 justify-content-center overflow-auto py-2 px-3 rounded-pill bg-black bg-opacity-40 border border-white border-opacity-10"
                            style={{ maxWidth: '90vw', zIndex: 1000000 }}
                            onClick={e => e.stopPropagation()}
                        >
                            {lightboxImages.map((img, idx) => (
                                <img
                                    key={idx}
                                    src={img.url}
                                    alt={`thumb-${idx}`}
                                    onClick={() => setLightboxIndex(idx)}
                                    style={{
                                        width: '46px',
                                        height: '34px',
                                        objectFit: 'cover',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        opacity: idx === lightboxIndex ? 1 : 0.45,
                                        border: idx === lightboxIndex ? '2px solid #6366f1' : '1px solid transparent',
                                        transform: idx === lightboxIndex ? 'scale(1.1)' : 'scale(1)',
                                        transition: 'all 0.2s ease'
                                    }}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </AdminLayout>
    );
};

export default AdminTicketDetail;
